import { useRef, useState, useEffect } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@/components/common/GoogleIconButton";
import TextField from "@/components/common/GoogleTextField";
import Autocomplete from "@/components/common/GoogleAutocomplete";
import { toast } from "sonner";
import { viewProfile, updateProfile } from "@/api/auth";
import { uploadFile } from "@/api/upload";
import { useResolvedUrl } from "@/hooks/useResolvedUrl";
import { useAuthStore } from "@/stores/auth";
import { FieldLabel } from "@/components/shared/FieldLabel";
import { MapsInput } from "@/components/common/MapsInput";
import { validateProfilePhone } from "@/lib/validation";

interface EditProfileProps {
  onDone: () => void;
  onBack: () => void;
}

interface AddressShape {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  cityCode?: string;
  state?: string;
  stateCode?: string;
  country?: string;
  countryCode?: string;
  pinCode?: string;
}

interface AddressSuggestion {
  addressLine: string;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  pinCode: string;
}

const DIAL_CODES = ["+91", "+65", "+1", "+44", "+61", "+32", "+971", "+49"];

export function EditProfile({ onDone, onBack }: EditProfileProps) {
  const storeUser = useAuthStore((s) => s.user);
  const inputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [address, setAddress] = useState<AddressShape>({});
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [saving, setSaving] = useState(false);
  const resolvedProfileImageUrl = useResolvedUrl(profileImageUrl);

  useEffect(() => {
    let cancelled = false;
    viewProfile()
      .then((data) => {
        if (cancelled) return;
        const raw = data as Record<string, unknown>;
        const address = (raw.address ?? {}) as AddressShape;
        setDisplayName(
          (raw.displayName as string) ?? storeUser?.displayName ?? "",
        );
        setEmail((raw.email as string) ?? storeUser?.email ?? "");
        setMobile((raw.mobile as string) ?? "");
        setDialCode((raw.dialCode as string) || "+91");
        setAddress({
          addressLine1: address?.addressLine1 ?? "",
          addressLine2: address?.addressLine2 ?? "",
          city: address?.city ?? "",
          cityCode: address?.cityCode ?? "",
          state: address?.state ?? "",
          stateCode: address?.stateCode ?? "",
          country: address?.country ?? "",
          countryCode: address?.countryCode ?? "",
          pinCode: address?.pinCode ?? "",
        });
        setProfileImageUrl((raw.profileImageUrl as string) ?? "");
      })
      .catch(() => {
        if (cancelled) return;
        setDisplayName(storeUser?.displayName ?? "");
        setEmail(storeUser?.email ?? "");
      });
    return () => {
      cancelled = true;
    };
  }, [storeUser]);

  const validate = (): boolean => {
    const next: { name?: string; phone?: string } = {};
    if (displayName.trim().length < 3) {
      next.name = "Name should be at least 3 characters";
    }
    if (mobile && !validateProfilePhone(dialCode, mobile)) {
      next.phone = "Enter a valid phone number";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    try {
      const res = await uploadFile(file);
      setProfileImageUrl(res.Location);
      toast.success("Profile picture updated");
    } catch {
      toast.error("Failed to upload profile picture");
    } finally {
      e.target.value = "";
    }
  };

  const setAddressField = (field: keyof AddressShape, value: string) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = (addr: AddressSuggestion) => {
    setAddress({
      ...address,
      addressLine1: addr.addressLine,
      city: addr.city,
      state: addr.state,
      stateCode: addr.stateCode,
      country: addr.country,
      countryCode: addr.countryCode,
      pinCode: addr.pinCode,
    });
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await updateProfile({
        displayName: displayName.trim(),
        firstName: displayName.trim(),
        email,
        mobile,
        dialCode,
        profileImageUrl,
        address: {
          addressLine1: address.addressLine1 ?? "",
          addressLine2: address.addressLine2 ?? "",
          city: address.city ?? "",
          cityCode: address.cityCode ?? "",
          state: address.state ?? "",
          stateCode: address.stateCode ?? "",
          country: address.country ?? "",
          countryCode: address.countryCode ?? "",
          pinCode: address.pinCode ?? "",
        },
      });
      const updated = res as Record<string, unknown>;
      if (storeUser && (updated?.email || updated?.displayName)) {
        useAuthStore.setState({
          user: {
            ...storeUser,
            displayName: (updated.displayName as string) ?? displayName.trim(),
            email: (updated.email as string) ?? email,
          },
        });
      }
      toast.success("Profile updated successfully");
      onDone();
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const initials = (displayName || "??")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <h2 className="text-base font-semibold tracking-tight">Edit Account</h2>
        <IconButton
          size="small"
          sx={{ width: 32, height: 32, color: "var(--muted-foreground)" }}
          onClick={onBack}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarChange}
        />

        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="group relative cursor-pointer bg-transparent border-0 p-0"
            aria-label="Change profile picture"
          >
            <Avatar
              src={resolvedProfileImageUrl || undefined}
              sx={{
                width: 64,
                height: 64,
                fontSize: "1.125rem",
                fontWeight: 600,
                color: "var(--primary)",
                backgroundColor: "var(--primary-8)",
                outline: "2px solid var(--border-40)",
                outlineOffset: "2px",
                transition: "outline-color 200ms",
                "&:hover": {
                  outlineColor: "var(--primary-30)",
                },
              }}
            >
              {initials}
            </Avatar>
            <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-card transition-colors group-hover:text-foreground">
              <Camera className="h-3.5 w-3.5" />
            </span>
          </button>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-name">Name</FieldLabel>
          <TextField
            id="edit-name"
            size="small"
            fullWidth
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            placeholder="Your name"
            error={!!errors.name}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-email">Email</FieldLabel>
          <TextField
            id="edit-email"
            size="small"
            fullWidth
            value={email}
            disabled
          />
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-phone">Contact</FieldLabel>
          <div className="flex gap-2">
            <Autocomplete
              options={DIAL_CODES.map((code) => ({ value: code, label: code }))}
              value={
                DIAL_CODES.map((code) => ({ value: code, label: code })).find(
                  (o) => o.value === dialCode,
                ) ?? undefined
              }
              onChange={(_, option) => setDialCode(option?.value ?? "")}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, s) => o.value === s.value}
              disableClearable
              autoHighlight
              openOnFocus
              className="w-24 shrink-0"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Code"
                  slotProps={{
                    ...params.slotProps,
                    htmlInput: {
                      ...params.slotProps.htmlInput,
                      "aria-label": "Dial code",
                    },
                  }}
                />
              )}
            />
            <TextField
              id="edit-phone"
              size="small"
              fullWidth
              value={mobile}
              onChange={(e) => {
                setMobile(e.target.value);
                setErrors((prev) => ({ ...prev, phone: undefined }));
              }}
              placeholder="Mobile number"
              error={!!errors.phone}
            />
          </div>
          {errors.phone && (
            <p className="text-xs text-destructive">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-address">Search Address</FieldLabel>
          <MapsInput
            value={address.addressLine1 ?? ""}
            onChange={handleAddressSelect}
            placeholder="Search and select address from Google Maps"
          />
          <p className="text-xs text-muted-foreground">
            Selecting a suggested address auto-fills the fields below
          </p>
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-address-line1">Address Line 1</FieldLabel>
          <TextField
            id="edit-address-line1"
            size="small"
            fullWidth
            value={address.addressLine1 ?? ""}
            onChange={(e) => setAddressField("addressLine1", e.target.value)}
            placeholder="Street address"
          />
        </div>

        <div className="space-y-2">
          <FieldLabel htmlFor="edit-address-line2">Address Line 2</FieldLabel>
          <TextField
            id="edit-address-line2"
            size="small"
            fullWidth
            value={address.addressLine2 ?? ""}
            onChange={(e) => setAddressField("addressLine2", e.target.value)}
            placeholder="Suite, floor, etc."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <FieldLabel htmlFor="edit-address-city">City</FieldLabel>
            <TextField
              id="edit-address-city"
              size="small"
              fullWidth
              value={address.city ?? ""}
              onChange={(e) => setAddressField("city", e.target.value)}
              placeholder="City"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="edit-address-state">State</FieldLabel>
            <TextField
              id="edit-address-state"
              size="small"
              fullWidth
              value={address.state ?? ""}
              onChange={(e) => setAddressField("state", e.target.value)}
              placeholder="State"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="edit-address-country">Country</FieldLabel>
            <TextField
              id="edit-address-country"
              size="small"
              fullWidth
              value={address.country ?? ""}
              onChange={(e) => setAddressField("country", e.target.value)}
              placeholder="Country"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="edit-address-pincode">Pin Code</FieldLabel>
            <TextField
              id="edit-address-pincode"
              size="small"
              fullWidth
              value={address.pinCode ?? ""}
              onChange={(e) => setAddressField("pinCode", e.target.value)}
              placeholder="Pin code"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border/60 px-4 py-3">
        <Button variant="outlined" onClick={onBack}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
